import { useEffect, useState } from 'react';
import { init, viewport, isTMA } from '@telegram-apps/sdk';
import { mockInitData } from 'templates/initData';
import { verifyToken } from 'utils/jwt';
import axios from 'utils/axios';
import { getViteEnv } from 'utils/viteEnv';

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready: () => void;
        expand: () => void;
        requestFullscreen: () => void;
        initData: string;
        initDataUnsafe?: {
          start_param?: string;
          [key: string]: unknown;
        };
      };
    };
    clearToken?: () => void;
  }
}

interface TelegramResponse {
  token: string;
  // Add other response properties as needed
}

interface UseTelegramTokenReturn {
  responseData: TelegramResponse | null;
}

const useTelegramToken = (): UseTelegramTokenReturn => {
  const [initData, setInitData] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<TelegramResponse | null>(null);
  const [currentPlayer, setCurrentPlayer] = useState<'player1' | 'player2'>(() => {
    return (localStorage.getItem('currentMockPlayer') as 'player1' | 'player2') || 'player2';
  });

  const useMockData = getViteEnv().VITE_USE_MOCK_INIT_DATA === 'true';
  localStorage.setItem('currentMockPlayer', 'player1');

  const loadTelegramData = async (): Promise<void> => {
    if (useMockData) {
      const playerData = currentPlayer === 'player2' ? mockInitData.initDataPlayer2 : mockInitData.initDataPlayer1;
      setInitData(playerData);
      return;
    }

    const tg = window.Telegram?.WebApp;
    if (!tg) {
      console.error('Telegram WebApp not found.');
      return;
    }

    tg.ready();
    tg.expand();
    tg.requestFullscreen();

    const currentSession = tg.initData;
    setInitData(currentSession);

    if (isTMA()) {
      init();
      if (viewport.mount.isAvailable() && !viewport.isMounted()) {
        await viewport.mount();
      }
      if (viewport.expand.isAvailable()) viewport.expand();
      if (viewport.requestFullscreen.isAvailable()) await viewport.requestFullscreen();
    }
  };

  useEffect(() => {
    loadTelegramData();
  }, [useMockData, currentPlayer]);

  useEffect(() => {
    if (!initData) return;

    const token = localStorage.getItem('token');
    if (verifyToken(token)) return;

    const sendInitDataToServer = async (): Promise<void> => {
      try {
        const response = await axios.post('/api/initdata', { initdata: initData }, { headers: { 'Content-Type': 'application/json' } });
        setResponseData(response.data);
      } catch (error) {
        console.error('Error sending initdata to the server:', error);
      }
    };

    sendInitDataToServer();
  }, [initData, currentPlayer]);

  return { responseData };
};

export default useTelegramToken;
